import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useRouteError } from 'react-router-dom';
import styled from 'styled-components';
import { IssueTag } from '../../../common/src/model.ts';
import { Pages } from '../../../common/src/pages.ts';
import * as server_api from '../../../common/src/server_api.ts';
import { localStorageGet, LocalStorageKey, localStorageRemove } from '../data/client_local_storage.ts';
import { LogMessages } from '../util/log_messages.tsx';
import { getPageFromLocation, getUrlForPage } from '../util/path.ts';

export const FailurePage = () => {
  const error = useRouteError() as Error;
  const errorMessage = `${error.message}${error.stack ? `\n${error.stack}` : ''}`;
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    void server_api.addIssue({
      timestamp: Date.now(),
      description: {
        tag: IssueTag.FailurePage,
        description: errorMessage,
      },
    });
  }, [errorMessage]);

  const onRestart = useCallback(() => {
    window.location.href = '/';
    return false;
  }, []);

  useEffect(() => {
    // OrgInfo updates can cause crashes in the planner dashboard, so nuke the stale data and try again
    const page = getPageFromLocation(location);
    if (page === Pages.Dashboard && localStorageGet(LocalStorageKey.OrgInfo)) {
      localStorageRemove(LocalStorageKey.OrgInfo);
      void navigate(getUrlForPage(page));
    }
    // eslint-disable-next-line
  }, []);

  return (
    <FailureWrapper>
      <FailureContent>
        <span>
          App failed to load.
          <br />
          A crash report has been sent to our engineers.
          <br />
          <a href="mailto:support@crescendosw.com" style={{ color: 'lightblue', textDecoration: 'underline' }}>
            Click here
          </a>{' '}
          to reach out to support if you have questions or concerns.
        </span>
      </FailureContent>
      <RestartButton onClick={onRestart}>Restart Application</RestartButton>
      <LogMessages error={errorMessage} />
    </FailureWrapper>
  );
};

const FailureWrapper = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  background-image: linear-gradient(#5200bb, black);
`;

const FailureContent = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  margin: auto;
  font-size: 1.2rem;
  color: white;
  padding: 5% 5%;
`;

const RestartButton = styled.button`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 1.3rem;
  padding: 10px 20px;
  border-radius: 30px;
  background-color: white;
  color: black;
  cursor: pointer;
`;
